import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Grid,
    useTheme,
    useMediaQuery,
    Autocomplete,
    TextField,
    OutlinedInput,
    IconButton,
    FormHelperText,
    CircularProgress,
    Dialog,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    IconArrowLeft,
    IconSeeding,
    IconTractor,
    IconPlant,
    IconRecycle,
    IconCamera,
    IconMapPinFilled,
    IconCheck,
    IconVideo,
    IconX,
    IconPlus,
    IconZoomIn,
    IconZoomOut,
    IconRefresh,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { colors, fonts } from '../../ColorFont/color';
import { url } from '../../ConstUrl/Url';
import NewHeaderNavWithSidebar from '../../components/HeaderNav/NewHeaderNavWithSidebar';
import CarbonCreditsCongratulations from './CarbonCreditsCongratulations';
import { useUser } from '../../context/UserContext';
import CarbonCreditsAuthGuard from '../../components/CarbonCredits/CarbonCreditsAuthGuard';
import { IconMapPin } from '@tabler/icons-react';

// ===== UNIVERSAL TOKENS =====
// Tokens used: carbon_upload_proof_bg, carbon_upload_input_bg, etc. (Setup in themes.js)

// ===== TRANSLATIONS payload directly nested as requested =====
const pageTranslations = {
    en: {
        back: "Back",
        title: "Upload Proof",
        subtitle: "Submit evidence for your carbon project",
        selectProjectLabel: "Select Project *",
        selectProjectPlaceholder: "Choose your carbon project",
        proofTypeLabel: "Proof Type *",
        proofTypes: {
            initialSetup: { title: "Initial Setup", subtitle: "Photos of land preparation" },
            farmActivity: { title: "Farm Activity", subtitle: "Ongoing farming practices" },
            plantation: { title: "Plantation/Crop", subtitle: "Current crop status" },
            residue: { title: "Residue Management", subtitle: "Stubble management proof" }
        },
        photosLabel: "Photos * (Max 5)",
        addPhoto: "Add Photo",
        videoUrlLabel: "Video URL (Optional)",
        videoUrlPlaceholder: "YouTube or Google Drive link",
        gpsLocationLabel: "GPS Location",
        gpsPlaceholder: "e.g., 18.5100, 73.8567",
        orEnterManually: "Or enter manually: Lat, Lng",
        noLocation: "No location captured",
        captureBtn: "Capture",
        locationHint: "Location helps verify proof authenticity",
        additionalNotesLabel: "Additional Notes",
        additionalNotesPlaceholder: "Describe what's shown in the photos...",
        verificationTipsTitle: "Verification Tips",
        tips: [
            "Take clear, well-lit photos",
            "Include landmarks or reference points",
            "Capture GPS for faster approval"
        ],
        submitBtn: "Submit Proof"
    },
    hi: {
        back: "वापस",
        title: "प्रमाण अपलोड करें",
        subtitle: "अपने कार्बन प्रोजेक्ट के लिए प्रमाण जमा करें",
        selectProjectLabel: "प्रोजेक्ट चुनें *",
        selectProjectPlaceholder: "अपना कार्बन प्रोजेक्ट चुनें",
        proofTypeLabel: "प्रमाण का प्रकार *",
        proofTypes: {
            initialSetup: { title: "प्रारंभिक सेटअप", subtitle: "भूमि तैयारी की तस्वीरें" },
            farmActivity: { title: "खेत की गतिविधि", subtitle: "चल रही कृषि पद्धतियां" },
            plantation: { title: "वृक्षारोपण/फसल", subtitle: "वर्तमान फसल की स्थिति" },
            residue: { title: "अवशेष प्रबंधन", subtitle: "पराली प्रबंधन का प्रमाण" }
        },
        photosLabel: "तस्वीरें * (अधिकतम 5)",
        addPhoto: "तस्वीर जोड़ें",
        videoUrlLabel: "वीडियो यूआरएल (वैकल्पिक)",
        videoUrlPlaceholder: "यूट्यूब या गूगल ड्राइव लिंक",
        gpsLocationLabel: "जीपीएस स्थान",
        gpsPlaceholder: "जैसे, 18.5100, 73.8567",
        orEnterManually: "या मैन्युअल रूप से दर्ज करें: Lat, Lng",
        noLocation: "कोई स्थान प्राप्त नहीं हुआ",
        captureBtn: "स्थान लें",
        locationHint: "स्थान प्रमाण की प्रामाणिकता सत्यापित करने में मदद करता है",
        additionalNotesLabel: "अतिरिक्त टिप्पणियाँ",
        additionalNotesPlaceholder: "तस्वीरों में क्या दिखाया गया है, उसका वर्णन करें...",
        verificationTipsTitle: "सत्यापन सुझाव",
        tips: [
            "स्पष्ट और अच्छी रोशनी वाली तस्वीरें लें",
            "लँडमार्क या संदर्भ बिंदु शामिल करें",
            "तेज़ अनुमोदन के लिए जीपीएस स्थान लें"
        ],
        submitBtn: "प्रमाण जमा करें"
    },
    mr: {
        back: "मागे",
        title: "पुरावा अपलोड करा",
        subtitle: "तुमच्या कार्बन प्रकल्पासाठी पुरावा सबमिट करा",
        selectProjectLabel: "प्रकल्प निवडा *",
        selectProjectPlaceholder: "तुमचा कार्बन प्रकल्प निवडा",
        proofTypeLabel: "पुराव्याचा प्रकार *",
        proofTypes: {
            initialSetup: { title: "प्रारंभिक सेटअप", subtitle: "जमीन तयारीचे फोटो" },
            farmActivity: { title: "शेतीतील उपक्रम", subtitle: "चालू शेती पद्धती" },
            plantation: { title: "लागवड/पीक", subtitle: "सध्याची पिकाची स्थिती" },
            residue: { title: "अवशेष व्यवस्थापन", subtitle: "काढणीपश्चात अवशेष व्यवस्थापन पुरावा" }
        },
        photosLabel: "फोटो * (जास्तीत जास्त ५)",
        addPhoto: "फोटो जोडा",
        videoUrlLabel: "व्हिडिओ URL (पर्यायी)",
        videoUrlPlaceholder: "YouTube किंवा Google Drive लिंक",
        gpsLocationLabel: "GPS ठिकाण",
        gpsPlaceholder: "उदा., १८.५१००, ७३.८५६७",
        orEnterManually: "किंवा स्वतः नोंदवा: Lat, Lng",
        noLocation: "ठिकाण नोंदवलेले नाही",
        captureBtn: "मिळवा",
        locationHint: "ठिकाणामुळे पुराव्याची सत्यता पडताळण्यास मदत होते",
        additionalNotesLabel: "अतिरिक्त नोंदी",
        additionalNotesPlaceholder: "फोटोंमध्ये काय दाखवले आहे त्याचे वर्णन करा...",
        verificationTipsTitle: "सत्यापन टिप्स",
        tips: [
            "स्पष्ट आणि चांगला प्रकाश असलेले फोटो घ्या",
            "ओळखीच्या खुणा(लँडमार्क) किंवा संदर्भ बिंदू समाविष्ट करा",
            "जलद मंजुरीसाठी GPS ठिकाण नोंदवा"
        ],
        submitBtn: "पुरावा सबमिट करा"
    }
};

const CustomInputLabel = ({ label }) => (
    <Typography sx={{
        fontFamily: fonts.publicSans || fonts.poppins,
        fontWeight: 500,
        fontSize: '16px',
        lineHeight: '24px',
        color: colors.carbon_text_main || '#0F1729',
        mb: '12px'
    }}>
        {label}
    </Typography>
);

const getInputStyles = (hasError) => ({
    backgroundColor: '#FFFFFF',
    borderRadius: '14px',
    height: '48px',
    boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
    border: `1px solid ${hasError ? '#EF4444' : (colors.carbon_border_solid || '#E0E5EB')}`,
    '&:hover': {
        borderColor: hasError ? '#EF4444' : (colors.carbon_border_selected || '#00B4D8')
    },
    '&.Mui-focused': {
        border: `1px solid ${hasError ? '#EF4444' : (colors.carbon_border_selected || '#00B4D8')}`,
        boxShadow: hasError ? `0 0 0 2px rgba(239, 68, 68, 0.2)` : `0 0 0 2px ${colors.carbon_bg_pill || '#B2EBF2'}`
    },
    '& fieldset': { border: 'none' },
    '& input': {
        fontFamily: fonts.publicSans || fonts.poppins,
        fontSize: '14px',
        color: colors.carbon_text_main || '#0F1729',
        padding: '0 16px',
        height: '100%',
        boxSizing: 'border-box'
    },
    '& input::placeholder': {
        color: colors.carbon_text_secondary || '#65758B',
        opacity: 1
    }
});

const ProofBox = ({ title, subtitle, icon, isSelected, onClick }) => (
    <Box
        onClick={onClick}
        sx={{
            cursor: 'pointer',
            backgroundColor: isSelected ? colors.carbon_bg_selected || '#E0F7FA' : colors.carbon_upload_proof_bg || '#FFFFFF',
            border: `1px solid ${isSelected ? colors.carbon_border_selected || '#00B4D8' : colors.carbon_border_solid || '#E0E5EB'}`,
            borderRadius: '12px',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            gap: '8px',
            minHeight: '114px',
            boxShadow: isSelected ? '0px 0px 0px 1px rgba(0, 180, 216, 0.4)' : '0px 1px 2px -1px rgba(0,0,0,0.1), 0px 1px 3px 0px rgba(0,0,0,0.1)',
            transition: 'all 0.2s ease-in-out',
            width: '100%',
            '&:hover': {
                borderColor: colors.carbon_border_selected || '#00B4D8',
                backgroundColor: isSelected ? undefined : '#FFFFFF'
            }
        }}
    >
        <Box sx={{ color: colors.carbon_text_main || '#0F1729' }}>
            {icon}
        </Box>
        <Box>
            <Typography sx={{
                fontFamily: fonts.publicSans || fonts.poppins,
                fontSize: '14px',
                fontWeight: 600,
                color: colors.carbon_text_main || '#0F1729',
                mb: '2px',
                lineHeight: 1.2
            }}>
                {title}
            </Typography>
            <Typography sx={{
                fontFamily: fonts.publicSans || fonts.poppins,
                fontSize: '12px',
                fontWeight: 400,
                color: colors.carbon_text_secondary || '#65758B',
                lineHeight: 1.2
            }}>
                {subtitle}
            </Typography>
        </Box>
    </Box>
);

// Compress a File using canvas → returns a Blob, target ~400KB JPEG
const compressImageFile = (file, maxWidthPx = 1280, quality = 0.75) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        img.onload = () => {
            URL.revokeObjectURL(objectUrl);
            let { width, height } = img;
            if (width > maxWidthPx) {
                height = Math.round((height * maxWidthPx) / width);
                width = maxWidthPx;
            }
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            canvas.toBlob(
                (blob) => {
                    if (!blob) return reject(new Error('Canvas compression failed'));
                    resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }));
                },
                'image/jpeg',
                quality
            );
        };
        img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('Image load failed')); };
        img.src = objectUrl;
    });
};

const CarbonCreditsUploadProof = ({ userData: propUserData, onBack }) => {
    const { userData, firebaseUser, loading } = useUser();
    const { i18n } = useTranslation();
    const currentLang = i18n.language || 'en';
    const tLocal = pageTranslations[currentLang] || pageTranslations['en'];
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    
    // Form Section Refs for Scrolling
    const projectSectionRef = useRef(null);
    const photoSectionRef = useRef(null);
    const videoSectionRef = useRef(null);
    const fileInputRef = useRef(null);

    // Field States
    const [project, setProject] = useState(null);
    const [projectError, setProjectError] = useState('');
    const [projectsList, setProjectsList] = useState([]);
    const [isLoadingProjects, setIsLoadingProjects] = useState(false);
    
    const [proofType, setProofType] = useState('initialSetup'); 
    const [videoUrl, setVideoUrl] = useState('');
    const [videoError, setVideoError] = useState('');
    const [notes, setNotes] = useState('');
    const [gpsLocation, setGpsLocation] = useState('');
    const [isLocating, setIsLocating] = useState(false);
    
    const [photos, setPhotos] = useState([]);
    const [photoError, setPhotoError] = useState('');
    const [previews, setPreviews] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showCongrats, setShowCongrats] = useState(false);

    // Image Viewer States
    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerImage, setViewerImage] = useState('');
    const [zoom, setZoom] = useState(1);

    const handleImageClick = (imageUrl) => {
        setViewerImage(imageUrl);
        setViewerOpen(true);
        setZoom(1);
    };

    // Fetch User projects
    useEffect(() => {
        if (userData?._id) {
            setIsLoadingProjects(true);
            fetch(`${url}/api/carbon-credits/projects/${userData._id}`)
                .then(res => res.json())
                .then(result => {
                    const records = (result.projects || []).map(p => ({
                        ...p,
                        district: p.farmDetails?.district || '',
                        state: p.farmDetails?.state || '',
                        village: p.farmDetails?.city || p.farmDetails?.village || '',
                        landSize: p.farmDetails?.landSize || '',
                        unit: p.farmDetails?.unit || '',
                    }));
                    setProjectsList(records);

                    // Check for projectId in URL and pre-select
                    const searchParams = new URLSearchParams(window.location.search);
                    const preSelectedId = searchParams.get('projectId');
                    if (preSelectedId) {
                        const preSelectedProject = records.find(p => p._id === preSelectedId);
                        if (preSelectedProject) setProject(preSelectedProject);
                    }
                })
                .catch(err => console.error("Error fetching projects:", err))
                .finally(() => setIsLoadingProjects(false));
        }
    }, [userData]);

    const handlePhotoClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handlePhotoChange = (e) => {
        const files = Array.from(e.target.files);

        if (photos.length + files.length > 5) {
            alert("Maximum 5 photos allowed");
            e.target.value = '';
            return;
        }

        // Store raw files for compression at submit time
        const newPhotos = [...photos, ...files];
        setPhotos(newPhotos);
        if (newPhotos.length > 0) setPhotoError('');

        // Generate previews from original files (instant, no quality loss for display)
        const newPreviews = files.map(file => URL.createObjectURL(file));
        setPreviews([...previews, ...newPreviews]);
        e.target.value = '';
    };

    const handleRemovePhoto = (index) => {
        const updatedPhotos = [...photos];
        updatedPhotos.splice(index, 1);
        setPhotos(updatedPhotos);

        const updatedPreviews = [...previews];
        URL.revokeObjectURL(updatedPreviews[index]);
        updatedPreviews.splice(index, 1);
        setPreviews(updatedPreviews);
    };

    const validateYouTube = (url) => {
        const regex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/;
        if (url && !regex.test(url)) {
            setVideoError("Please enter a valid YouTube URL (e.g., https://youtu.be/xxxx)");
            return false;
        }
        setVideoError("");
        return true;
    };

    const handleGetLocation = () => {
        setIsLocating(true);
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    setGpsLocation(`${pos.coords.latitude.toFixed(6)}, ${pos.coords.longitude.toFixed(6)}`);
                    setIsLocating(false);
                },
                (err) => {
                    console.error(err);
                    alert("Unable to fetch location. Please try manually.");
                    setIsLocating(false);
                }
            );
        } else {
            alert("Geolocation not supported");
            setIsLocating(false);
        }
    };

    const handleSubmit = async () => {
        let firstErrorRef = null;

        // Reset and Validate
        let isValid = true;

        if (!project) {
            setProjectError("Please select a project");
            if (!firstErrorRef) firstErrorRef = projectSectionRef;
            isValid = false;
        } else {
            setProjectError("");
        }

        if (photos.length === 0) {
            setPhotoError("Please upload at least one photo");
            if (!firstErrorRef) firstErrorRef = photoSectionRef;
            isValid = false;
        } else {
            setPhotoError("");
        }

        const isYoutubeValid = validateYouTube(videoUrl);
        if (!isYoutubeValid) {
            if (!firstErrorRef) firstErrorRef = videoSectionRef;
            isValid = false;
        }

        if (!isValid) {
            if (firstErrorRef && firstErrorRef.current) {
                firstErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
            return;
        }

        // Map camelCase keys → exact DB enum values expected by the schema
        const proofTypeMap = {
            initialSetup: 'Initial Setup',
            farmActivity:  'Farm Activity',
            plantation:    'Plantation/Crop',
            residue:       'Residue Management',
        };

        setIsSubmitting(true);
        try {
            // Compress all photos client-side before uploading
            // This keeps the multipart payload well under Nginx's body-size limit
            const compressedPhotos = await Promise.all(
                photos.map(file => compressImageFile(file, 1280, 0.75))
            );

            const formData = new FormData();
            formData.append('projectId', project._id);
            formData.append('proofType', proofTypeMap[proofType] || proofType);
            if (videoUrl) formData.append('videoUrl', videoUrl);
            if (gpsLocation) formData.append('gpsLocation', gpsLocation);
            if (notes) formData.append('additionalNotes', notes);
            compressedPhotos.forEach(file => formData.append('photos', file));

            const response = await fetch(`${url}/api/carbon-credits/proofs`, {
                method: 'POST',
                body: formData
                // Do NOT set Content-Type manually — browser sets it with boundary for multipart
            });

            if (!response.ok) {
                let errMsg = 'Failed to submit proof';
                try { const errData = await response.json(); errMsg = errData.message || errMsg; } catch (_) {}
                throw new Error(errMsg);
            }

            setIsSubmitting(false);
            setShowCongrats(true);
        } catch (error) {
            console.error('Proof submission error:', error);
            alert(`Submission failed: ${error.message}`);
            setIsSubmitting(false);
        }
    };

    const handleBack = () => {
        if (onBack) onBack();
        else navigate(-1);
    };

    return (
        <CarbonCreditsAuthGuard 
            userData={userData} 
            firebaseUser={firebaseUser} 
            loading={loading}
        >
            <Box sx={{
            width: '100%',
            minHeight: '100vh',
            backgroundColor: '#FFFFFF',
            display: 'flex',
            justifyContent: 'center',
            pt: { xs: 2, md: 4 },
            pb: { xs: 10, md: 6 } 
        }}>
            <Box sx={{
                width: '100%',
                maxWidth: '680px',
                px: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '24px' 
            }}>

                {/* ===== NAVIGATION & HEADER ===== */}
                <Box>
                    <Button
                        onClick={handleBack}
                        variant="outlined"
                        startIcon={<IconArrowLeft size={18} stroke={2.5} />}
                        sx={{
                            borderRadius: '50px',
                            px: 3,
                            height: '52px',
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontWeight: 700,
                            fontSize: '15px',
                            textTransform: 'none',
                            color: colors.carbon_text_main || '#1C252E',
                            borderColor: '#E0E5EB',
                            mb: '24px',
                            minWidth: '120px',
                            '&:hover': {
                                borderColor: '#919EAB',
                                backgroundColor: '#F4F6F8',
                            },
                        }}
                    >
                        {tLocal.back}
                    </Button>

                    <Typography sx={{
                        fontFamily: fonts.manrope || fonts.poppins,
                        fontWeight: 700,
                        fontSize: '24px',
                        lineHeight: '32px',
                        letterSpacing: '-0.6px',
                        color: colors.carbon_text_main || '#0F1729',
                        mb: '4px'
                    }}>
                        {tLocal.title}
                    </Typography>

                    <Typography sx={{
                        fontFamily: fonts.publicSans || fonts.poppins,
                        fontWeight: 400,
                        fontSize: '16px',
                        lineHeight: '24px',
                        color: colors.carbon_text_secondary || '#65758B'
                    }}>
                        {tLocal.subtitle}
                    </Typography>
                </Box>


                {/* ===== PROJECT SELECTION ===== */}
                <Box sx={{ mt: '8px' }} ref={projectSectionRef}>
                    <CustomInputLabel label={tLocal.selectProjectLabel} />
                    <Autocomplete
                        options={projectsList}
                        getOptionLabel={(option) => {
                            if (typeof option === 'string') return option;
                            const name = option.projectName || 'Carbon Project';
                            const loc = [option.village, option.district].filter(Boolean).join(', ');
                            return loc ? `${name} (${loc})` : name;
                        }}
                        renderOption={(props, option) => (
                            <Box component="li" {...props} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important', py: '10px !important', px: '14px !important' }}>
                                <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#1C252E', lineHeight: 1.3 }}>
                                    {option.projectName || 'Carbon Project'}
                                </Typography>
                                {(option.village || option.district) && (
                                    <Typography sx={{ fontSize: '12px', color: '#94A3B8', mt: '2px' }}>
                                        📍 {[option.village, option.district].filter(Boolean).join(', ')}
                                    </Typography>
                                )}
                            </Box>
                        )}
                        value={project}
                        onChange={(e, val) => {
                            setProject(val);
                            if (val) setProjectError("");
                        }}
                        loading={isLoadingProjects}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={tLocal.selectProjectPlaceholder}
                                error={!!projectError}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        ...getInputStyles(!!projectError),
                                        paddingRight: '39px !important',
                                        paddingLeft: '14px !important'
                                    }
                                }}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {isLoadingProjects ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                    {projectError && <FormHelperText error sx={{ ml: 0 }}>{projectError}</FormHelperText>}
                </Box>


                {/* ===== PROOF TYPES ===== */}
                <Box>
                    <CustomInputLabel label={tLocal.proofTypeLabel} />
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <ProofBox
                                title={tLocal.proofTypes.initialSetup.title}
                                subtitle={tLocal.proofTypes.initialSetup.subtitle}
                                icon={<IconSeeding size={28} stroke={1.5} />}
                                isSelected={proofType === 'initialSetup'}
                                onClick={() => setProofType('initialSetup')}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <ProofBox
                                title={tLocal.proofTypes.farmActivity.title}
                                subtitle={tLocal.proofTypes.farmActivity.subtitle}
                                icon={<IconTractor size={28} stroke={1.5} />}
                                isSelected={proofType === 'farmActivity'}
                                onClick={() => setProofType('farmActivity')}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <ProofBox
                                title={tLocal.proofTypes.plantation.title}
                                subtitle={tLocal.proofTypes.plantation.subtitle}
                                icon={<IconPlant size={28} stroke={1.5} />}
                                isSelected={proofType === 'plantation'}
                                onClick={() => setProofType('plantation')}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <ProofBox
                                title={tLocal.proofTypes.residue.title}
                                subtitle={tLocal.proofTypes.residue.subtitle}
                                icon={<IconRecycle size={28} stroke={1.5} />}
                                isSelected={proofType === 'residue'}
                                onClick={() => setProofType('residue')}
                            />
                        </Grid>
                    </Grid>
                </Box>


                {/* ===== PHOTO UPLOAD (MAX 5) ===== */}
                <Box ref={photoSectionRef}>
                    <CustomInputLabel label={tLocal.photosLabel} />
                    <input 
                        type="file" 
                        multiple 
                        accept="image/*" 
                        ref={fileInputRef} 
                        style={{ display: 'none' }} 
                        onChange={handlePhotoChange}
                    />
                    
                    <Grid container spacing={2}>
                        {previews.map((preview, index) => (
                            <Grid item xs={6} sm={4} md={3} key={index}>
                                <Box sx={{
                                    position: 'relative',
                                    width: '100%',
                                    aspectRatio: '1/1',
                                    borderRadius: '16px',
                                    overflow: 'hidden',
                                    border: `1px solid ${colors.carbon_border_solid || '#E0E5EB'}`
                                }}>
                                    <img 
                                        src={preview} 
                                        alt={`Preview ${index}`} 
                                        style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }} 
                                        onClick={() => handleImageClick(preview)}
                                    />
                                    <IconButton
                                        onClick={() => handleRemovePhoto(index)}
                                        sx={{
                                            position: 'absolute',
                                            top: '4px',
                                            right: '4px',
                                            backgroundColor: 'rgba(255,255,255,0.8)',
                                            padding: '4px',
                                            '&:hover': { backgroundColor: 'rgba(255,255,255,1)' }
                                        }}
                                    >
                                        <IconX size={16} color="#0F1729" />
                                    </IconButton>
                                </Box>
                            </Grid>
                        ))}
                        
                        {photos.length < 5 && (
                            <Grid item xs={6} sm={4} md={3}>
                                <Box 
                                    onClick={handlePhotoClick}
                                    sx={{
                                        width: '100%',
                                        aspectRatio: '1/1',
                                        border: `2px dashed ${colors.carbon_border_solid || '#E0E5EB'}`,
                                        borderRadius: '16px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px',
                                        cursor: 'pointer',
                                        transition: 'border-color 0.2s',
                                        '&:hover': { borderColor: colors.carbon_border_selected || '#00B4D8' }
                                    }}
                                >
                                    <IconPlus size={32} color={colors.carbon_text_secondary || '#65758B'} stroke={1.5} />
                                    <Typography sx={{
                                        fontFamily: fonts.publicSans || fonts.poppins,
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        color: colors.carbon_text_secondary || '#65758B'
                                    }}>
                                        {tLocal.addPhoto}
                                    </Typography>
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                    {photoError && <FormHelperText error sx={{ ml: 0, mt: 1 }}>{photoError}</FormHelperText>}
                </Box>

                {/* ===== VIDEO URL (OPTIONAL) ===== */}
                <Box ref={videoSectionRef}>
                    <CustomInputLabel label={tLocal.videoUrlLabel} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <IconVideo size={20} color={colors.carbon_text_secondary || '#65758B'} stroke={1.5} />
                        <OutlinedInput
                            fullWidth
                            value={videoUrl}
                            onChange={(e) => {
                                setVideoUrl(e.target.value);
                                if (videoError) validateYouTube(e.target.value);
                            }}
                            onBlur={(e) => validateYouTube(e.target.value)}
                            error={!!videoError}
                            placeholder={tLocal.videoUrlPlaceholder}
                            sx={{ ...getInputStyles(), height: '40px' }}
                        />
                    </Box>
                    {videoError && <FormHelperText error sx={{ ml: '28px' }}>{videoError}</FormHelperText>}
                </Box>

                {/* ===== GPS LOCATION VERIFICATION ===== */}
                <Box>
                    <CustomInputLabel label={tLocal.gpsLocationLabel} />
                    <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                            <OutlinedInput
                                fullWidth
                                value={gpsLocation}
                                onChange={(e) => setGpsLocation(e.target.value)}
                                placeholder={tLocal.gpsPlaceholder}
                                sx={getInputStyles()}
                            />
                            <Typography sx={{
                                fontFamily: fonts.publicSans || fonts.poppins,
                                fontWeight: 400,
                                fontSize: '12px',
                                color: colors.carbon_text_secondary || '#65758B',
                                mt: '8px'
                            }}>
                                {tLocal.orEnterManually}
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={handleGetLocation}
                            disabled={isLocating}
                            sx={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '14px',
                                border: `1px solid ${colors.carbon_border_solid || '#E0E5EB'}`,
                                backgroundColor: '#FFFFFF',
                                color: colors.carbon_text_main || '#0F1729',
                                '&:hover': { backgroundColor: '#FFFFFF' }
                            }}
                        >
                            {isLocating ? <CircularProgress size={20} /> : <IconMapPin size={20} />}
                        </IconButton>
                    </Box>
                </Box>


                {/* ===== ADDITIONAL NOTES ===== */}
                <Box>
                    <Typography sx={{
                        fontFamily: fonts.publicSans || fonts.poppins,
                        fontWeight: 500,
                        fontSize: '14px',
                        lineHeight: '14px',
                        color: colors.carbon_text_main || '#0F1729',
                        mb: '8px'
                    }}>
                        {tLocal.additionalNotesLabel}
                    </Typography>
                    <OutlinedInput
                        multiline
                        minRows={3}
                        fullWidth
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder={tLocal.additionalNotesPlaceholder}
                        sx={{
                            ...getInputStyles(),
                            height: 'auto',
                            minHeight: '78px',
                            padding: '12px',
                            '& input': { padding: 0 }
                        }}
                    />
                </Box>


                {/* ===== VERIFICATION TIPS ===== */}
                <Box sx={{
                    width: '100%',
                    backgroundColor: colors.carbon_upload_tips_bg || '#FFFBEB',
                    border: `1px solid ${colors.carbon_upload_tips_border || '#FDE68A'}`,
                    borderRadius: '16px',
                    p: '16px'
                }}>
                    <Box sx={{ display: 'flex', gap: '8px', mb: '8px', alignItems: 'center' }}>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10 6.66667V10M10 13.3333H10.0083M18.3333 10C18.3333 14.6024 14.6024 18.3333 10 18.3333C5.39763 18.3333 1.66667 14.6024 1.66667 10C1.66667 5.39763 5.39763 1.66667 10 1.66667C14.6024 1.66667 18.3333 5.39763 18.3333 10Z" stroke="#B45309" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <Typography sx={{
                            color: colors.carbon_upload_tips_text || '#92400E',
                            fontSize: '14px',
                            fontWeight: 500,
                            fontFamily: fonts.publicSans || fonts.poppins,
                            lineHeight: '20px'
                        }}>
                            {tLocal.verificationTipsTitle}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', pl: '28px' }}>
                        {tLocal.tips.map((tip, index) => (
                            <Typography key={index} sx={{
                                color: colors.carbon_upload_tips_bullet || '#B45309',
                                fontSize: '14px',
                                fontWeight: 400,
                                fontFamily: fonts.publicSans || fonts.poppins,
                                lineHeight: '20px'
                            }}>
                                • {tip}
                            </Typography>
                        ))}
                    </Box>
                </Box>

                {/* End of content spacing */}
                <Box sx={{ height: '32px' }} />

                {/* ===== BOTTOM ACTION BUTTONS ===== */}
                <Box sx={{ 
                    width: '100%', 
                    display: 'flex', 
                    justifyContent: 'center', 
                    gap: '16px', 
                    flexDirection: isMobile ? 'column' : 'row',
                    mb: '40px'
                }}>
                    <Button
                        onClick={handleBack}
                        variant="outlined"
                        sx={{
                            borderRadius: '50px',
                            px: { xs: 3, md: 6 },
                            height: '52px',
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontWeight: 700,
                            fontSize: '15px',
                            textTransform: 'none',
                            color: colors.carbon_text_main || '#1C252E',
                            borderColor: colors.carbon_border_solid || '#E0E5EB',
                            minWidth: { sm: '180px' },
                            '&:hover': {
                                borderColor: colors.carbon_border_selected || '#919EAB',
                                backgroundColor: '#F4F6F8',
                            },
                        }}
                    >
                        {tLocal.back}
                    </Button>

                    <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        sx={{
                            borderRadius: '50px',
                            px: { xs: 3, md: 6 },
                            height: '52px',
                            backgroundColor: colors.carbon_bg_main || '#00B4D8',
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontWeight: 700,
                            fontSize: '15px',
                            textTransform: 'none',
                            color: '#FFFFFF',
                            minWidth: { sm: '220px' },
                            boxShadow: '0px 4px 12px rgba(0, 180, 216, 0.2)',
                            '&:hover': {
                                backgroundColor: '#009DC0',
                                boxShadow: '0px 6px 16px rgba(0, 180, 216, 0.3)',
                                transform: 'translateY(-1px)'
                            },
                            '&.Mui-disabled': {
                                background: colors.carbon_bg_pill || '#E0F7FA',
                                opacity: 0.7
                            },
                            transition: 'all 0.2s ease-in-out'
                        }}
                    >
                        {isSubmitting ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            <>
                                <IconCamera stroke={2.2} size={20} style={{ marginRight: '8px' }} />
                                {tLocal.submitBtn}
                            </>
                        )}
                    </Button>
                </Box>

                <CarbonCreditsCongratulations 
                    open={showCongrats} 
                    onClose={() => setShowCongrats(false)} 
                    isProof={true}
                />

            </Box>

            {/* Image Viewer Lightbox */}
            <Dialog
                open={viewerOpen}
                onClose={() => setViewerOpen(false)}
                maxWidth="md"
                PaperProps={{
                    sx: { 
                        backgroundColor: 'rgba(15, 23, 24, 0.98)', 
                        backdropFilter: 'blur(12px)',
                        boxShadow: 'none',
                        borderRadius: '28px',
                        overflow: 'hidden',
                        m: 2,
                        height: '80vh',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                    }
                }}
            >
                <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', p: 1 }}>
                    {/* Viewer Header */}
                    <Box sx={{ 
                        width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', 
                        p: 2, position: 'absolute', top: 0, left: 0, zIndex: 10 
                    }}>
                        <Box sx={{ display: 'flex', gap: 1, backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '12px', p: 0.5 }}>
                            <IconButton onClick={() => setZoom(prev => Math.min(prev + 0.5, 4))} sx={{ color: '#fff' }}>
                                <IconZoomIn size={22} />
                            </IconButton>
                            <IconButton onClick={() => setZoom(prev => Math.max(prev - 0.5, 0.5))} sx={{ color: '#fff' }}>
                                <IconZoomOut size={22} />
                            </IconButton>
                            <IconButton onClick={() => setZoom(1)} sx={{ color: '#fff' }}>
                                <IconRefresh size={22} />
                            </IconButton>
                        </Box>
                        <IconButton onClick={() => setViewerOpen(false)} sx={{ backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' } }}>
                            <IconX size={24} />
                        </IconButton>
                    </Box>

                    {/* Image Container */}
                    <Box sx={{ 
                        width: '100%', height: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', 
                        overflow: 'auto', p: 4, mt: 6,
                        '&::-webkit-scrollbar': { display: 'none' }
                    }}>
                        <img 
                            src={viewerImage} 
                            alt="Full proof" 
                            style={{ 
                                maxWidth: '100%', 
                                maxHeight: '100%', 
                                objectFit: 'contain',
                                transform: `scale(${zoom})`,
                                transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                borderRadius: '8px'
                            }} 
                        />
                    </Box>
                </Box>
            </Dialog>
        </Box>
        </CarbonCreditsAuthGuard>
    );
};

export default CarbonCreditsUploadProof;
